import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Video, Plus, Trash2, Copy, ExternalLink } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import { useConfig } from '../../contexts/ConfigContext';
import './UserZoomPage.css';

const emptySlot = () => ({ date: '', time_from: '', time_to: '' });

const formatDateRu = (iso) => {
    if (!iso) return '—';
    const [y, m, d] = String(iso).split('-');
    if (!y || !m || !d) return iso;
    return `${d}.${m}.${y}`;
};

const formatCreatedAt = (v) => {
    if (!v) return '—';
    return new Date(v).toLocaleString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const bookingTitle = (item) => {
    if (item.group_id) {
        return item.group_nomination || item.group_name || 'Группа';
    }
    return 'Личная ссылка';
};

const ZoomCard = ({ item, onCopy, onDelete, showDelete }) => (
    <article className="user-zoom-history-card">
        <div className="user-zoom-history-top">
            <strong>{bookingTitle(item)}</strong>
            <span className="user-zoom-history-date">{formatCreatedAt(item.created_at)}</span>
        </div>
        {item.group_id && (
            <span className="user-zoom-kind-badge">Вебинар / группа</span>
        )}
        {item.organizer_code && (
            <p className="user-zoom-organizer">
                <strong>Код организатора:</strong> {item.organizer_code}
            </p>
        )}
        {item.description && (
            <pre className="user-zoom-description">{item.description}</pre>
        )}
        <div className="user-zoom-link-row">
            <a href={item.zoom_url} target="_blank" rel="noopener noreferrer">
                {item.zoom_url}
            </a>
            <button type="button" className="btn btn-secondary" onClick={() => onCopy(item.zoom_url)}>
                <Copy size={16} />
            </button>
            {showDelete && (
                <button
                    type="button"
                    className="btn btn-secondary"
                    title="Удалить"
                    onClick={() => onDelete(item)}
                >
                    <Trash2 size={16} />
                </button>
            )}
        </div>
        <ul className="user-zoom-slots-list">
            {(item.slots || []).map((s, i) => (
                <li key={i}>
                    {formatDateRu(s.date)}
                    {(s.time_from || s.time_to) && (
                        <> · {[s.time_from, s.time_to].filter(Boolean).join('–')}</>
                    )}
                </li>
            ))}
        </ul>
    </article>
);

const UserZoomPage = () => {
    const { user, loading: authLoading } = useAuth();
    const { config, loading: configLoading } = useConfig();
    const queryClient = useQueryClient();
    const [slots, setSlots] = useState([emptySlot()]);
    const [submitting, setSubmitting] = useState(false);
    const [lastLink, setLastLink] = useState(null);

    const isNasledie = config?.code === 'nasledie';
    const canUse = !authLoading && !configLoading && Boolean(user) && isNasledie;

    const { data, isLoading } = useQuery(
        ['myZoomBookings'],
        async () => {
            const res = await axios.get(`${API_BASE_URL}/zoom/my-bookings`, { withCredentials: true });
            return res.data;
        },
        { enabled: canUse }
    );

    if (authLoading || configLoading) {
        return <div className="loading"><div className="spinner" /></div>;
    }

    if (!user) return <Navigate to="/login" replace />;
    if (!isNasledie) return <Navigate to="/" replace />;

    const history = data?.items || [];
    const groupLinks = history.filter((i) => i.group_id);
    const personalLinks = history.filter((i) => !i.group_id);

    const updateSlot = (index, field, value) => {
        setSlots((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
    };

    const addSlot = () => setSlots((prev) => [...prev, emptySlot()]);
    const removeSlot = (index) => {
        setSlots((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const cleaned = slots
            .map((s) => ({
                date: (s.date || '').trim(),
                time_from: (s.time_from || '').trim() || null,
                time_to: (s.time_to || '').trim() || null
            }))
            .filter((s) => s.date);

        if (!cleaned.length) {
            toast.warning('Укажите хотя бы одну дату');
            return;
        }

        setSubmitting(true);
        try {
            const res = await axios.post(
                `${API_BASE_URL}/zoom/book`,
                { slots: cleaned },
                { withCredentials: true }
            );
            setLastLink(res.data);
            toast.success('Ссылка Zoom выдана');
            setSlots([emptySlot()]);
            queryClient.invalidateQueries(['myZoomBookings']);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Не удалось получить ссылку');
        } finally {
            setSubmitting(false);
        }
    };

    const copyLink = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Ссылка скопирована');
        } catch (_) {
            toast.info(url);
        }
    };

    const handleDeleteBooking = async (item) => {
        if (!window.confirm('Удалить эту выдачу ссылки?')) return;
        try {
            await axios.delete(`${API_BASE_URL}/zoom/my-bookings/${item.id}`, {
                withCredentials: true
            });
            toast.success('Удалено');
            if (lastLink?.id === item.id) setLastLink(null);
            queryClient.invalidateQueries(['myZoomBookings']);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Не удалось удалить');
        }
    };

    return (
        <div className="user-zoom-page">
            <div className="user-zoom-header">
                <h1>
                    <Video size={28} />
                    Zoom
                </h1>
                <p>Укажите даты и время — система выдаст свободную ссылку. Ссылки групп появляются после «Получить Zoom» в карточке группы.</p>
            </div>

            <form className="user-zoom-form" onSubmit={handleSubmit}>
                <h2>Получить ссылку</h2>
                {slots.map((slot, index) => (
                    <div key={index} className="user-zoom-slot-row">
                        <label>
                            <span>Дата</span>
                            <input
                                type="date"
                                value={slot.date}
                                onChange={(e) => updateSlot(index, 'date', e.target.value)}
                                required
                            />
                        </label>
                        <label>
                            <span>С</span>
                            <input
                                type="time"
                                value={slot.time_from}
                                onChange={(e) => updateSlot(index, 'time_from', e.target.value)}
                            />
                        </label>
                        <label>
                            <span>До</span>
                            <input
                                type="time"
                                value={slot.time_to}
                                onChange={(e) => updateSlot(index, 'time_to', e.target.value)}
                            />
                        </label>
                        <button
                            type="button"
                            className="btn btn-secondary user-zoom-slot-remove"
                            onClick={() => removeSlot(index)}
                            disabled={slots.length <= 1}
                            title="Удалить день"
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                ))}
                <div className="user-zoom-form-actions">
                    <button type="button" className="btn btn-secondary" onClick={addSlot}>
                        <Plus size={16} /> Ещё день
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                        {submitting ? 'Подбор…' : 'Получить ссылку'}
                    </button>
                </div>
            </form>

            {lastLink?.zoom_url && (
                <div className="user-zoom-result">
                    <h2>Ваша ссылка</h2>
                    {lastLink.organizer_code && (
                        <p className="user-zoom-organizer">
                            <strong>Код организатора:</strong> {lastLink.organizer_code}
                        </p>
                    )}
                    {lastLink.description && (
                        <pre className="user-zoom-description">{lastLink.description}</pre>
                    )}
                    <div className="user-zoom-link-row">
                        <a href={lastLink.zoom_url} target="_blank" rel="noopener noreferrer">
                            {lastLink.zoom_url}
                        </a>
                        <button type="button" className="btn btn-secondary" onClick={() => copyLink(lastLink.zoom_url)}>
                            <Copy size={16} />
                        </button>
                        <a
                            className="btn btn-primary"
                            href={lastLink.zoom_url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <ExternalLink size={16} /> Открыть
                        </a>
                    </div>
                    <ul className="user-zoom-slots-list">
                        {(lastLink.slots || []).map((s, i) => (
                            <li key={i}>
                                {formatDateRu(s.date)}
                                {(s.time_from || s.time_to) && (
                                    <> · {[s.time_from, s.time_to].filter(Boolean).join('–')}</>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="user-zoom-history">
                <h2>Ссылки групп</h2>
                {isLoading ? (
                    <div className="loading"><div className="spinner" /></div>
                ) : groupLinks.length === 0 ? (
                    <p className="user-zoom-empty">Пока нет ссылок групп. Откройте группу → вкладка Zoom → «Получить Zoom».</p>
                ) : (
                    <div className="user-zoom-history-list">
                        {groupLinks.map((item) => (
                            <ZoomCard
                                key={item.id}
                                item={item}
                                onCopy={copyLink}
                                onDelete={handleDeleteBooking}
                                showDelete
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className="user-zoom-history">
                <h2>Личные ссылки</h2>
                {isLoading ? (
                    <div className="loading"><div className="spinner" /></div>
                ) : personalLinks.length === 0 ? (
                    <p className="user-zoom-empty">Пока нет личных выдач.</p>
                ) : (
                    <div className="user-zoom-history-list">
                        {personalLinks.map((item) => (
                            <ZoomCard
                                key={item.id}
                                item={item}
                                onCopy={copyLink}
                                onDelete={handleDeleteBooking}
                                showDelete
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserZoomPage;
